import SlideFrame from "@/components/SlideFrame";
import { Scale, Check, Mail, ArrowRight, ShieldCheck, BookOpen, Briefcase, FileText, Eye, Users, Building2, Heart } from "lucide-react";
import { packFor } from "@/lib/industryPack";
import { DarkeDiscountCallout, useDarkeDiscount } from "@/components/DarkeDiscount";

/* Per-firm branded dashboard mocks for the law-firm pitch. */
const FIRM_DASHBOARDS = {
  "dykema":              { src: "/brand/legal/dykema.png",  caption: "Dykema · firm dashboard" },
  "mavacy":              { src: "/brand/legal/mavacy.png",  caption: "Mavacy LLP · firm dashboard" },
  "chapman-and-cutler":  { src: "/brand/legal/chapman.png", caption: "Chapman and Cutler LLP · firm dashboard" },
  "brom-law":            { src: "/brand/legal/brom.png",    caption: "Brom Law PLLC · firm dashboard" },
};

const CLIENT_PORTAL = { src: "/brand/legal/client-portal.png", caption: "Your client portal · hours · matters · documents" };

/* ============================ The Offer · CompliSun Legal PLC ============================ */
export function S_LegalHelp({ idx, total, prospect }) {
  const slug = (prospect?.slug || "").toLowerCase();
  const pack = packFor(prospect);
  const isLawFirm = /law|attorney/i.test(prospect?.industry || "");
  const isLegalCorporate = pack.kind === "1B";

  // 2A — law-firm channel sees the new "two-view operations platform" offer
  if (pack.lawFirm) return <LawFirmOffer idx={idx} total={total} prospect={prospect} />;
  // 1B — corporate sees the 3-tier services menu
  if (isLegalCorporate) return <LegalServicesMenu idx={idx} total={total} prospect={prospect} />;

  // -- Existing flow for 2A/2B (law-firm) + 6 (bonus) prospects --
  const perUser = 59;
  const install = 5000;
  const dash = isLawFirm
    ? (FIRM_DASHBOARDS[slug] || { src: "/brand/legal/mavacy.png", caption: "CompliSun Legal · firm dashboard" })
    : CLIENT_PORTAL;

  const headline = isLawFirm ? (
    <>Run your firm on <em className="italic text-sun-600">CompliSun Legal PLC.</em></>
  ) : (
    <>Need legal help, <em className="italic text-sun-600">too?</em></>
  );

  const subhead = isLawFirm
    ? "The platform built for law firms. Your matters, your associates, your billing — privileged by architecture, AI-ready, audit-ready."
    : `${prospect?.company_name || "Your company"} deserves outside counsel that earns trust with full transparency and real-time collaboration. CompliSun Legal PLC is Colin's law firm — plugged into the platform you just walked, so every hour, every matter, every document is visible to you in real time.`;

  const benefits = isLawFirm
    ? [
        { icon: ShieldCheck, t: "Outside-counsel coverage",   b: "Co-counsel + overflow for your firm — drafted by lawyers, not interns." },
        { icon: BookOpen,    t: "Regulatory tracking",        b: "Plain-English alerts when something changes — federal, state, agency bulletins." },
        { icon: Briefcase,   t: "Matter management",          b: "Each matter sits in its own bucket. Privilege preserved by architecture." },
        { icon: FileText,    t: "Document review queue",      b: "Drop a contract, get redlines + risk flagged in hours, not weeks." },
      ]
    : [
        { icon: Eye,         t: "Total transparency",         b: "Live dashboard: your hours, your budget, every active matter, every document — never a surprise invoice." },
        { icon: Users,       t: "Real-time collaboration",    b: "Shared queues with your team. Tasks, redlines, approvals — no inbox archaeology." },
        { icon: BookOpen,    t: "Regulatory tracking",        b: "Plain-English alerts when something in your space changes — federal, state, agency." },
        { icon: FileText,    t: "Document review",            b: "Drop a contract, get redlines + risk flagged in hours, not weeks." },
      ];

  return (
    <SlideFrame idx={idx} total={total} kicker={isLawFirm ? "CompliSun Legal PLC" : "Bonus · Need Legal Help"} dark={false}>
      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-7">
          <div className="inline-flex items-center gap-2 mono text-[10px] uppercase tracking-[0.22em] text-sun-700 mb-4">
            <Scale className="h-3.5 w-3.5" strokeWidth={1.5} /> / CompliSun Legal · PLC
          </div>
          <h2 className="serif font-light text-4xl md:text-5xl leading-[1.05] tracking-tight" data-testid="legal-headline">
            {headline}
          </h2>

          {!isLawFirm && (
            <div className="mt-5 inline-flex flex-wrap items-center gap-2" data-testid="legal-transparency-badge">
              <span className="mono text-[10px] uppercase tracking-[0.22em] bg-sun-500/15 border border-sun-500/40 text-sun-700 rounded-full px-3 py-1.5">
                Built on transparency
              </span>
              <span className="mono text-[10px] uppercase tracking-[0.22em] bg-ember-500/15 border border-ember-500/40 text-ember-700 rounded-full px-3 py-1.5">
                & real-time collaboration
              </span>
            </div>
          )}

          <p className="mt-5 text-ink-900/75 text-base md:text-lg leading-relaxed max-w-2xl">{subhead}</p>

          <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {benefits.map(({ icon: Icon, t, b }) => (
              <div key={t} className="border border-ink-900/10 rounded-md p-4 bg-white/70" data-testid={`legal-benefit-${t.toLowerCase().replace(/\W+/g,'-')}`}>
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-sun-700" strokeWidth={1.5} />
                  <div className="serif text-base text-ink-900">{t}</div>
                </div>
                <div className="mt-1.5 text-sm text-ink-900/70 leading-relaxed">{b}</div>
              </div>
            ))}
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <a
              href={`mailto:colindarke@complisun.com?subject=${encodeURIComponent("CompliSun Legal PLC — interested")}&body=${encodeURIComponent(`Hi Colin,\n\n${prospect?.company_name || "We"} want to learn more about CompliSun Legal PLC.\n\n— ${prospect?.contact_name || ""}`)}`}
              data-testid="legal-cta-email"
              className="inline-flex items-center gap-2 bg-ink-900 hover:bg-ink-800 text-cream rounded-md px-5 py-3 serif text-base transition-colors"
            >
              <Mail className="h-4 w-4" strokeWidth={1.5} />
              Email Colin about Legal
              <ArrowRight className="h-4 w-4" />
            </a>
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55">
              colindarke@complisun.com · 313 · 617 · 4473
            </div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-5">
          <div className="border border-sun-500/50 rounded-md p-6 bg-gradient-to-br from-sun-500/10 to-ember-500/5 shadow-sm" data-testid="legal-price-card">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700">/ pricing · simple</div>
            <div className="mt-3 flex items-baseline gap-2 flex-wrap">
              <div className="serif text-5xl text-ink-900">${perUser}<span className="text-base font-normal text-ink-900/65">/lawyer/mo</span></div>
            </div>
            <p className="mt-1 text-sm text-ink-900/70">
              Per-user access — <strong>not per client</strong>. {isLawFirm ? "Bring your whole bench under one license." : "Bring our whole bench to bear on your matters."}
            </p>

            <div className="mt-4 pt-4 border-t border-ink-900/10 flex items-baseline gap-2 flex-wrap">
              <div className="serif text-2xl text-ink-900">+ ${install.toLocaleString()}</div>
              <div className="text-sm text-ink-900/65">one-time install</div>
            </div>
            <p className="mt-1 text-sm text-ink-900/70">
              Covers the 45-day setup: {isLawFirm ? "your branding, your matter taxonomy, your roles + retention rules." : "your branded client portal, your matter taxonomy, your team's access roles."}
            </p>

            <ul className="mt-5 space-y-2 text-sm text-ink-900/85">
              {(isLawFirm
                ? ["Unlimited matters", "Claude wired in — no data leaves your env", "Co-branded client portals", "AML / UDAAP / Reg-Z / SOC-2 templates included"]
                : ["Unlimited matters", "Live hours-used + budget meter", "Shared task queue with your team", "Document review SLAs in writing"]
              ).map((t) => (
                <li key={t} className="flex items-start gap-2"><Check className="h-4 w-4 text-sun-700 mt-0.5 flex-shrink-0" /><span>{t}</span></li>
              ))}
            </ul>
          </div>

          <div className="mt-4 border border-ink-900/10 rounded-md overflow-hidden bg-white shadow-sm">
            <div className="bg-cream-100 grid place-items-center" style={{ aspectRatio: "16 / 10" }}>
              <img src={dash.src} alt={dash.caption} className="w-full h-full object-contain" data-testid="legal-dashboard-preview" />
            </div>
            <div className="px-4 py-3 mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 border-t border-ink-900/8">
              {dash.caption}
            </div>
          </div>
        </div>
      </div>
    </SlideFrame>
  );
}

/* ====================== 1B · Three-tier Legal Services menu ====================== */
function LegalServicesMenu({ idx, total, prospect }) {
  const slug = (prospect?.slug || "").toLowerCase();
  const isSloan = slug === "sloan";
  const [darkeActive] = useDarkeDiscount(slug);
  // Founding price is 20% off standard. Kim-Darke easter egg bumps to 25% off.
  const discount = isSloan && darkeActive ? 0.25 : 0.20;
  const priceWithDiscount = (std) => Math.round(std * (1 - discount));

  const baseTiers = [
    {
      id: "hotline",
      name: "Hotline",
      tagline: "For growing companies that need ongoing legal access",
      standard: 1250,
      hoursLine: "Up to $3,000/mo additional work",
      effective: null,
      features: [
        "Weekly legal office hours (Zoom)",
        "Dedicated Slack channel",
        "100+ legal templates",
        "AI drafting chatbot access",
        "CompliSun platform access",
        "$250/hr for deep-dive work",
        "Email support",
      ],
      featured: false,
    },
    {
      id: "counsel",
      name: "Counsel",
      tagline: "For companies needing regular legal support",
      standard: 4500,
      hoursLine: "12.5 hours included · $360/hr effective ($288/hr w/ discount)",
      effective: "Up to 5 unused hours roll over monthly",
      features: [
        "Everything in Hotline",
        "12.5 hours included per month",
        "Priority response (same day)",
        "Contract review included (2/mo)",
        "Compliance calendar & alerts",
        "Up to 5 hours rollover",
        "Quarterly business reviews",
      ],
      featured: true,
    },
    {
      id: "gc",
      name: "Fractional Team",
      tagline: "Fractional senior attorney or senior compliance team member, embedded with you",
      standard: 9500,
      hoursLine: "33 hours included · $288/hr effective ($230/hr w/ discount)",
      effective: "Up to 5 unused hours roll over monthly",
      features: [
        "Everything in Counsel",
        "33 hours included per month",
        "Weekly strategy sessions",
        "Board meeting support",
        "M&A transaction support",
        "Up to 5 hours rollover",
        "Custom compliance programs",
      ],
      featured: false,
    },
  ];
  const tiers = baseTiers.map((t) => ({ ...t, founding: priceWithDiscount(t.standard) }));
  const discountLabel = Math.round(discount * 100);

  return (
    <SlideFrame idx={idx} total={total} kicker="CompliSun Legal PLC" dark={false}>
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 mono text-[10px] uppercase tracking-[0.22em] text-sun-700 mb-3">
          <Scale className="h-3.5 w-3.5" strokeWidth={1.5} /> / CompliSun Legal · PLC
        </div>
        <h2 className="serif font-light text-4xl md:text-5xl leading-[1.05] tracking-tight" data-testid="legal-headline">
          Need legal help? <em className="italic text-sun-600">We built a firm for that, too.</em>
        </h2>
        <p className="mt-4 text-ink-900/75 text-base md:text-lg leading-relaxed max-w-3xl">
          CompliSun's clients often need real legal counsel — not just compliance work. So we built
          CompliSun Legal PLC: a modern law firm structured to serve growing companies as fractional
          General Counsel, a legal hotline, or project-based outside counsel. You pick the engagement
          that fits.
        </p>
      </div>

      {isSloan && (
        <div className="mb-5" data-testid="sloan-darke-wrap">
          <DarkeDiscountCallout
            slug={slug}
            question="Do you know Kimberly Darke?"
            hint="(say yes for a special rate)"
            unlockedHeadline="Darke discount unlocked"
            unlockedSub="25% off CompliSun Legal — locked in for the first 12 months."
            noResponseText="No worries — Founding Client pricing (20% off) still stands."
            testId="sloan-darke"
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="legal-tiers">
        {tiers.map((t) => (
          <div
            key={t.id}
            data-testid={`legal-tier-${t.id}`}
            className={`relative border rounded-md p-5 flex flex-col ${
              t.featured
                ? "border-sun-500 bg-gradient-to-br from-sun-500/10 to-ember-500/5 shadow-sm ring-2 ring-sun-500/30"
                : "border-ink-900/12 bg-white/70"
            }`}
          >
            {t.featured && (
              <div className="absolute -top-2.5 left-5 mono text-[9px] uppercase tracking-[0.22em] bg-sun-500 text-ink-900 rounded-full px-2.5 py-1">
                Recommended
              </div>
            )}
            <div className="absolute -top-2.5 right-5 mono text-[9px] uppercase tracking-[0.20em] bg-ember-500 text-cream rounded-full px-2.5 py-1">
              {discountLabel}% {discount > 0.20 ? "Family" : "Founding"} · 12 mo
            </div>

            <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700 mt-3">{t.name}</div>
            <div className="serif text-sm text-ink-900/70 mt-1 leading-snug">{t.tagline}</div>

            <div className="mt-4">
              <div className="flex items-baseline gap-2">
                <div className="serif text-4xl text-ink-900">${t.founding.toLocaleString()}</div>
                <div className="text-sm text-ink-900/65">/mo</div>
              </div>
              <div className="mt-1 mono text-[10px] uppercase tracking-[0.18em] text-ink-900/55">
                <span className="line-through opacity-70 mr-2">${t.standard.toLocaleString()}/mo</span>
                <span className="text-ember-600">{discount > 0.20 ? "Family rate" : "Founding Client price"}</span>
              </div>
              <div className="mt-2 text-xs text-ink-900/75 leading-relaxed">{t.hoursLine}</div>
              {t.effective && (
                <div className="mt-1 text-[11px] text-ink-900/60 italic leading-snug">{t.effective}</div>
              )}
            </div>

            <ul className="mt-4 space-y-1.5 text-sm text-ink-900/85 flex-1">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="h-3.5 w-3.5 text-sun-700 mt-1 flex-shrink-0" strokeWidth={2.5} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Alternative engagement model — blended hourly */}
      <div className="mt-4 border border-ink-900/12 rounded-md p-4 md:p-5 bg-white/70 flex flex-col md:flex-row items-start md:items-center gap-4" data-testid="blended-hourly-card">
        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-sun-500/15 border border-sun-500/40 grid place-items-center">
          <Briefcase className="h-5 w-5 text-sun-700" strokeWidth={1.6} />
        </div>
        <div className="flex-1">
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700">/ alternative · pay-as-you-go</div>
          <div className="serif text-lg text-ink-900 leading-tight mt-0.5">
            Prefer to skip the monthly retainer?{" "}
            <span className="text-sun-700 italic">
              We do a blended hourly rate too — ${Math.round(350 * (1 - discount))}/hr {discount > 0.20 ? "with the family rate" : "with the Founding Client discount"}.
            </span>
          </div>
          <p className="text-sm text-ink-900/65 leading-relaxed mt-1">
            Standard blended rate is <strong className="text-ink-900">$350/hr</strong> across attorneys + senior compliance staff —
            same {discountLabel}% off comes off the top.{" "}
            <em className="italic">No monthly minimum, no included-hour math.</em>
          </p>
        </div>
        <div className="flex-shrink-0 text-right">
          <div className="mono text-[10px] uppercase tracking-[0.18em] text-ink-900/55">Effective rate</div>
          <div className="serif text-3xl text-ink-900 leading-none mt-0.5">
            <span className="line-through text-ink-900/35 text-base mr-2">$350</span>${Math.round(350 * (1 - discount))}<span className="text-sm text-ink-900/55">/hr</span>
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
        <p className="text-xs text-ink-900/65 leading-relaxed max-w-3xl">
          All plans include: 3-month initial commitment, then month-to-month. Overage billed at
          $250/hour (Hotline/Counsel) or $200/hour (Fractional Team).{" "}
          <strong className="text-ink-900">{discount > 0.20 ? "Family rate" : "Founding Client Discount"} applies to first 12 months only.</strong>
        </p>
        <a
          href="https://calendly.com/colindarke-complisun/complisun-legal-intro"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="legal-cta-schedule"
          className="inline-flex items-center gap-2 bg-ink-900 hover:bg-ink-800 text-cream rounded-md px-5 py-3 serif text-base transition-colors"
        >
          Schedule legal consultation
          <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </SlideFrame>
  );
}


/* ====================== 2A · Law-Firm Two-View Offer ====================== */
function LawFirmOffer({ idx, total, prospect }) {
  const pack = packFor(prospect);
  return (
    <SlideFrame idx={idx} total={total} kicker="The Offer" dark={false}>
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 mono text-[10px] uppercase tracking-[0.22em] text-sun-700 mb-3">
          <Scale className="h-3.5 w-3.5" strokeWidth={1.5} /> / law firm platform
        </div>
        <h2 className="serif font-light text-4xl md:text-5xl leading-[1.04] tracking-tight" data-testid="legal-headline">
          An operations system <em className="italic text-sun-600">your firm runs on.</em>
          <br />
          A client experience <em className="italic text-sun-600">your clients want to visit.</em>
        </h2>
        <p className="mt-4 text-ink-900/75 text-base md:text-lg leading-relaxed max-w-4xl">
          {pack.offerIntro}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="lawfirm-offer-columns">
        {/* For Your Firm */}
        <div className="border border-sun-500/40 rounded-md p-5 bg-gradient-to-br from-sun-500/10 to-transparent" data-testid="lawfirm-offer-firm">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="h-5 w-5 text-sun-700" strokeWidth={1.5} />
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700">For your firm</div>
          </div>
          <ul className="space-y-2 text-sm text-ink-900/85">
            {(pack.offerFirmFeatures || []).map((f) => (
              <li key={f} className="flex items-start gap-2">
                <Check className="h-3.5 w-3.5 text-sun-700 mt-1 flex-shrink-0" strokeWidth={2.5} />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* For Your Clients */}
        <div className="border border-ember-500/40 rounded-md p-5 bg-gradient-to-br from-ember-500/10 to-transparent" data-testid="lawfirm-offer-client">
          <div className="flex items-center gap-2 mb-3">
            <Heart className="h-5 w-5 text-ember-700" strokeWidth={1.5} />
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-ember-700">For your clients</div>
          </div>
          <ul className="space-y-2 text-sm text-ink-900/85">
            {(pack.offerClientFeatures || []).map((f) => (
              <li key={f} className="flex items-start gap-2">
                <Check className="h-3.5 w-3.5 text-ember-600 mt-1 flex-shrink-0" strokeWidth={2.5} />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {pack.offerStickiness && (
        <div
          className="mt-6 border border-ink-900/12 bg-ink-900 text-cream rounded-md px-5 py-4"
          data-testid="lawfirm-stickiness"
        >
          <div className="serif text-lg md:text-xl leading-snug italic">
            "{pack.offerStickiness}"
          </div>
        </div>
      )}
    </SlideFrame>
  );
}
